import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import {
  FaBolt,
  FaTools,
  FaSnowflake,
  FaPhone,
  FaFire,
  FaTint,
  FaHammer,
  FaPaintRoller,
  FaLeaf,
  FaDoorOpen,
  FaShieldAlt,
  FaPlug,
  FaCouch,
  FaWater,
  FaSun,
  FaClock,
  FaCheckCircle,
  FaUsers,
  FaStar,
} from "react-icons/fa";
import { SiHomeassistantcommunitystore } from "react-icons/si";
import { Routes, Route, Link } from "react-router-dom";
import logo from "./assets/ypglogo.png";
import "./firebase";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsAndConditions from "./TermsAndConditions";
import emailjs from "@emailjs/browser";

// ─────────────────────────────────────────────
// Simple fade-up animation
// ─────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

// Helper for initials in testimonial avatars
function getInitials(name) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

// ─────────────────────────────────────────────
// Animated Stat Counter
// ─────────────────────────────────────────────
function StatCounter({ value, suffix = "" }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (latest) => {
  // If original value has decimals, keep one decimal
  if (String(value).includes(".")) {
    return Number(latest).toFixed(1);
  }
  // Otherwise use normal whole numbers
  return Math.floor(latest).toLocaleString();
});

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(motionValue, value, {
      duration: 1.3,
      ease: "easeOut",
    });
    return controls.stop;
  }, [isInView, motionValue, value]);

  return (
    <div ref={ref} className="flex items-baseline justify-center gap-1">
      <motion.span className="text-3xl sm:text-4xl font-semibold">
        {rounded}
      </motion.span>
      {suffix && (
        <span className="text-2xl sm:text-3xl font-semibold">{suffix}</span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Data
// ─────────────────────────────────────────────
const stats = [
  { label: "Years Serving Memphis", value: 7, suffix: "+", icon: FaClock },
  { label: "Projects Completed", value: 7500, suffix: "+", icon: FaCheckCircle },
  { label: "Customers Served", value: 5000, suffix: "+", icon: FaUsers },
  { label: "Google Rating", value: 4.9, suffix: "/5", icon: FaStar },
];

const services = [
  {
    icon: FaBolt,
    label: "Electrical",
    description:
      "Panel upgrades, outlets, lighting, troubleshooting, and safe repairs for older homes.",
  },
  {
    icon: FaTint,
    label: "Plumbing",
    description:
      "Leaks, clogs, fixtures, water heaters, and general plumbing repairs and replacements.",
  },
  {
    icon: FaSnowflake,
    label: "HVAC",
    description:
      "Heating and cooling maintenance, repairs, and replacements to keep your home comfortable.",
  },
  {
    icon: FaHammer,
    label: "Remodeling",
    description:
      "Kitchens, bathrooms, and interior updates designed for comfort, function, and value.",
  },
  {
    icon: FaPaintRoller,
    label: "Painting",
    description:
      "Interior and exterior painting with clean lines, proper prep, and durable finishes.",
  },
  {
    icon: FaLeaf,
    label: "Landscaping",
    description:
      "Yard cleanups, trimming, mulching, and basic exterior maintenance for curb appeal.",
  },
  {
    icon: FaDoorOpen,
    label: "Doors & Windows",
    description:
      "Installations, replacements, and adjustments to improve comfort and efficiency.",
  },
  {
    icon: FaShieldAlt,
    label: "Roofing & Gutters",
    description:
      "Repairs, small replacements, and gutter work to protect your home from the elements.",
  },
  {
    icon: FaTools,
    label: "Handyman",
    description:
      "Punch lists, small fixes, and general maintenance so minor issues don’t stack up.",
  },
  {
    icon: FaPlug,
    label: "Smart Home & Lighting",
    description:
      "Smart switches, thermostats, and lighting upgrades for modern, convenient living.",
  },

  {
    icon: SiHomeassistantcommunitystore,
    label: "Property Management Support",
    description:
      "Reliable partner for ongoing maintenance across your portfolio of properties.",
  },
  {
    icon: FaWater,
    label: "Pressure Washing",
    description:
      "Driveways, patios, siding, and more to keep your exterior looking fresh.",
  },
  
];

const testimonials = [
  {
    name: "Josh H.",
    rating: 5,
    text: "“Your Property Guys handled our HVAC repair and a bathroom remodel. Professional, on time, and the work was excellent.”",
    imageUrl: "https://scontent-atl3-2.xx.fbcdn.net/v/t39.30808-6/473733529_10170460888975591_7589996179058969020_n.jpg?_nc_cat=104&ccb=1-7&_nc_sid=b04a48&_nc_ohc=KrhnhFdRHBUQ7kNvwGPwpxS&_nc_oc=Adm3hSIib-JD3eew_S2Xi-Ru4Umrtrxgvceuo8QsI9ob0HtEpovi8dQo8-YDizkJ3Ydbcc8DLWR5OvRuQeqxjR2j&_nc_zt=23&_nc_ht=scontent-atl3-2.xx&_nc_gid=d3OdxzWb8KvI0Vdk19GZmw&oh=00_AfjKoH074e9AC2o_LmoWxgl6108CzgYMEny18lKnlgk7tQ&oe=6926878F",
  },
  {
    name: "Clarence T.",
    rating: 5,
    text: "“They’ve become our go-to for rental turns. Clear communication, fair pricing, and they show up when they say they will.”",
    imageUrl: "https://scontent-atl3-3.xx.fbcdn.net/v/t39.30808-6/542649945_796893096131367_8693757844279872180_n.jpg?stp=cp6_dst-jpg_tt6&_nc_cat=107&ccb=1-7&_nc_sid=833d8c&_nc_ohc=pWDQFlArGycQ7kNvwE4P30A&_nc_oc=Adm8La3SLc00Q5SdpOOSgo-Mk4_INGUCeCUVslqlVzKvV6UprbhfaW0FnZa7jPJHMqXOsTaRSohuGm8kF2SGXvUp&_nc_zt=23&_nc_ht=scontent-atl3-3.xx&_nc_gid=0LIDE7jduCeVSq2hdGfzBw&oh=00_Afh8w5tmEudiR_9mnnzlHLJVtM0t_iXrDBZKfYDBosc1UQ&oe=692687BD",
  },
  {
    name: "Ben T.",
    rating: 5,
    text: "“It’s so nice to call one company for almost everything. Electrical, plumbing, small fixes – they’ve done it all for us.”",
    imageUrl: "https://scontent-atl3-3.xx.fbcdn.net/v/t39.30808-6/514618448_10163344264386753_6971138936059165678_n.jpg?_nc_cat=109&ccb=1-7&_nc_sid=a5f93a&_nc_ohc=OElfnkriIGgQ7kNvwHkXxb3&_nc_oc=AdleFjG2M7hXdG9_dGKEKsgeXPrsiLivLPr2RSomKdMtpqznplFQzbz2SEICcPazarFtRLcBcewxl9fwL1gBljRX&_nc_zt=23&_nc_ht=scontent-atl3-3.xx&_nc_gid=cqkqsT1JIiQS8cy0xMe36A&oh=00_AfhqSkRkVu_o94hgppzXO1LiGGqW8g3rM8bVpFJIjvMZfA&oe=6926717C",
  },
];

// ─────────────────────────────────────────────
// Estimate Modal (cleaner)
// ─────────────────────────────────────────────

function EstimateModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    details: "",
    smsConsent: false,
  });

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
const SERVICE_ID  = process.env.EMAILJS_SERVICE_ID  || "service_jszer4l";
const TEMPLATE_ID = process.env.EMAILJS_TEMPLATE_ID || "template_7ik6bfd";
const PUBLIC_KEY  = process.env.EMAILJS_PUBLIC_KEY  || "5od5RiWaN2AsVbXhJ";
const PRIVATE_KEY = process.env.EMAILJS_PRIVATE_KEY || "15m18OD3P3EBWvSihUew3";
const DEFAULT_TO  = process.env.EMAILJS_TO_EMAIL    || "info@elevatecoachingrei.com";

const cfg = useMemo(() => {
  return {
    serviceId: "service_jszer4l",
    templateId: "template_c3z9z2t",
    publicKey: "5od5RiWaN2AsVbXhJ",
    toEmail: process.env.REACT_APP_ESTIMATE_TO_EMAIL || "info@yourpropertyguys.com",
  };
}, []);

  if (!open) return null;

  const update = (key) => (e) => {
    const value =
      e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: value }));
  };

  const sendEstimateEmail = async () => {
    setError("");

    if (!cfg.serviceId || !cfg.templateId || !cfg.publicKey) {
      throw new Error(
        "Missing EmailJS env vars. Check SERVICE_ID / TEMPLATE_ID / PUBLIC_KEY."
      );
    }

    // template params must match your EmailJS template variables
    const params = {
      to_email: cfg.toEmail || "info@yourpropertyguys.com",
      subject: "NEW ESTIMATE REQUEST",
      name: form.name || "(not provided)",
      phone: form.phone,
      email: form.email || "(not provided)",
      details: form.details,
      sms_consent: form.smsConsent ? "YES" : "NO",
      submitted_at: new Date().toLocaleString("en-US"),
      page_url: window.location.href,
    };

    // EmailJS browser SDK (public key only)
    await emailjs.send(cfg.serviceId, cfg.templateId, params, cfg.publicKey);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-3xl bg-white p-7 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold tracking-tight">
              Get an Estimate Today
            </h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Share a few details about your project and the best way to reach
              you. We’ll follow up with next steps.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-slate-700"
          >
            Close
          </button>
        </div>

        {sent ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <p className="text-sm font-semibold text-slate-900">
              Request received ✅
            </p>
            <p className="text-sm text-slate-600 mt-1">
              We’ll follow up shortly.
            </p>
            <button
              onClick={() => {
                setSent(false);
                setForm({
                  name: "",
                  phone: "",
                  email: "",
                  details: "",
                  smsConsent: false,
                });
                onClose();
              }}
              className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-ypgGreen px-5 py-3 text-sm font-semibold text-white hover:bg-ypgGreenDark transition"
            >
              Done
            </button>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setSending(true);
              try {
                await sendEstimateEmail();
                setSent(true);
              } catch (err) {
                setError(err?.message || "Failed to send email. Try again.");
              } finally {
                setSending(false);
              }
            }}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Name (optional)
                </label>
                <input
                  value={form.name}
                  onChange={update("name")}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ypgGreen focus:border-ypgGreen"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-600 mb-1">Phone</label>
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ypgGreen focus:border-ypgGreen"
                  placeholder="(___) ___-____"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Email (optional)
              </label>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ypgGreen focus:border-ypgGreen"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1">
                What do you need done?
              </label>
              <textarea
                rows="4"
                value={form.details}
                onChange={update("details")}
                className="w-full rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ypgGreen focus:border-ypgGreen"
                placeholder="Tell us about your project, property, and timing."
                required
              />
            </div>

            {/* SMS consent block (required for A2P) */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-start gap-3">
                <input
  type="checkbox"
  checked={form.smsConsent}
  onChange={update("smsConsent")}
  className="mt-1 h-4 w-4 rounded border-slate-300 text-ypgGreen focus:ring-ypgGreen"
/>
                <span className="text-[12px] leading-relaxed text-slate-700">
                  <span className="font-semibold">SMS Consent:</span> By checking
                  this box and providing your phone number, you agree to receive
                  text messages from{" "}
                  <span className="font-semibold">Your Property Guys</span> about
                  appointment scheduling, estimate follow-ups, and project
                  updates. <span className="font-semibold">Message frequency varies.</span>{" "}
                  <span className="font-semibold">Message &amp; data rates may apply.</span>{" "}
                  Reply <span className="font-semibold">STOP</span> to unsubscribe,{" "}
                  <span className="font-semibold">HELP</span> for help. Consent is not a
                  condition of purchase. View our{" "}
                  <Link to="/privacypolicy" className="text-ypgGreen hover:underline">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" className="text-ypgGreen hover:underline">
                    Terms &amp; Conditions
                  </Link>
                  .
                </span>
              </label>
            </div>

            {error ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={sending}
              className="mt-2 inline-flex w-full items-center justify-center rounded-full bg-ypgGreen px-5 py-3 text-sm font-semibold text-white hover:bg-ypgGreenDark transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending..." : "Get an Estimate Today"}
            </button>

            <p className="text-[11px] text-slate-500 mt-2 text-center">
              By submitting, you agree to be contacted by Your Property Guys about your project.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────
// Home Page Content
// ─────────────────────────────────────────────
function HomePage({ onOpenEstimate }) {
  return (
    <>
      {/* HERO */}
      <section className="py-16 sm:py-20 bg-white">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="mx-auto max-w-3xl px-4 text-center space-y-8"
        >
          <div className="flex flex-col items-center gap-4">
            <img
              src={logo}
              alt="Your Property Guys"
              className="h-28 sm:h-32 w-auto"
            />
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              Memphis · Home &amp; Property Services
            </p>
          </div>

          <div className="space-y-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
              One trusted team for{" "}
              <span className="text-ypgGreen">every project</span> in your home.
            </h1>
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
              Your Property Guys has completed over 7,500 projects for more than
              5,000 customers across Memphis, with a 4.9/5 rating on Google.
              One call, one crew, almost anything your property needs.
            </p>
          </div>

          <div className="flex flex-col items-center gap-3 pt-1">
            <button
              onClick={onOpenEstimate}
              className="inline-flex items-center justify-center rounded-full bg-ypgGreen px-12 py-3.5 text-sm sm:text-base font-semibold text-white hover:bg-ypgGreenDark transition"
            >
              Get an Estimate Today
            </button>
            <p className="text-xs text-slate-500 max-w-xs">
              Licensed and insured. Clear communication from first call to final
              walkthrough.
            </p>
          </div>
        </motion.div>
      </section>

      {/* STATS */}
      <section className="py-12 bg-white">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="mx-auto max-w-4xl px-4"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {stats.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="space-y-3">
                  <Icon className="mx-auto text-2xl text-ypgGreen" />
                  <StatCounter value={item.value} suffix={item.suffix} />
                  <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                    {item.label}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-white">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="mx-auto max-w-5xl px-4 text-center space-y-10"
        >
          <div className="space-y-3 max-w-2xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              Services
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Almost anything your home or property needs,
              <br className="hidden sm:block" />
              handled by one team.
            </h2>
            <p className="text-sm text-slate-600">
              From everyday repairs to larger projects, Your Property Guys is
              the single point of contact for homeowners, investors, and
              property managers.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-8 gap-y-10">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  className="group relative flex flex-col items-center text-center"
                >
                  <div className="flex items-center justify-center h-16 w-16 mb-4 rounded-full border border-slate-200 text-ypgGreen group-hover:border-ypgGreen group-hover:shadow-md transition">
                    <Icon className="text-3xl" />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-slate-800">
                    {s.label}
                  </p>
                  <p className="pointer-events-none mt-2 w-full text-[11px] text-slate-500 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 translate-y-1 transition">
                    {s.description}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 bg-white">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="mx-auto max-w-5xl px-4 text-center space-y-8"
        >
          <div className="space-y-3 max-w-2xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
              What customers say
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Memphis homeowners and investors trust 
              <br/>Your Property Guys.
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
  {testimonials.map((t) => (
    <div
      key={t.name}
      className="space-y-3 flex flex-col items-center text-center"
    >
      {/* Avatar – image if provided, otherwise initials */}
      <div className="h-12 w-12 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center">
        {t.imageUrl ? (
          <img
            src={t.imageUrl}
            alt={t.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-xs font-semibold text-slate-500">
            {getInitials(t.name)}
          </span>
        )}
      </div>

      <div className="flex items-center justify-center gap-1">
        {Array.from({ length: t.rating }).map((_, i) => (
          <FaStar key={i} className="text-ypgGreen text-xs" />
        ))}
        <span className="text-xs text-slate-500">Google Review</span>
      </div>

      <p className="text-sm text-slate-700">{t.text}</p>
      <p className="text-xs font-semibold text-slate-900">{t.name}</p>
    </div>
  ))}
</div>
        </motion.div>
      </section>

      {/* SIMPLE CTA SECTION */}
      <section className="py-20 bg-white">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="mx-auto max-w-3xl px-4 text-center space-y-6"
        >
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            Ready to start your next project?
          </h2>
          <p className="text-sm text-slate-600 max-w-xl mx-auto">
            Share what you&apos;re planning and we&apos;ll provide a clear,
            straightforward estimate with no surprises.
          </p>
          <button
            onClick={onOpenEstimate}
            className="inline-flex items-center justify-center rounded-full bg-ypgGreen px-10 py-3.5 text-sm sm:text-base font-semibold text-white hover:bg-ypgGreenDark transition"
          >
            Get an Estimate Today
          </button>
        </motion.div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Shell with header, footer, routes
// ─────────────────────────────────────────────
function App() {
  const [estimateOpen, setEstimateOpen] = useState(false);

  const openEstimate = () => setEstimateOpen(true);
  const closeEstimate = () => setEstimateOpen(false);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HEADER */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Your Property Guys"
              className="w-auto"
              style={{ height: "5.25rem" }} // bigger header logo
            />
            <div className="hidden sm:block leading-tight">
              <p className="text-sm font-medium tracking-tight">
                Your Property Guys
              </p>
              <p className="text-[11px] text-slate-500">
                Memphis · Home &amp; Property Services
              </p>
            </div>
          </div>
        <div className="flex flex-col items-end gap-1 text-xs sm:text-sm">
  <button
    onClick={openEstimate}
    className="inline-flex min-w-[230px] items-center justify-center rounded-full bg-ypgGreen px-8 py-3 font-semibold text-white hover:bg-ypgGreenDark transition"
  >
    Get an Estimate Today
  </button>

  <a
    href="tel:+19014415099"
    className="inline-flex min-w-[230px] items-center justify-center gap-2 rounded-full border border-ypgGreen text-ypgGreen px-8 py-3 font-semibold hover:bg-ypgGreen hover:text-white transition"
  >
    <FaPhone className="text-sm" />
    <span>901-441-5099</span>
  </a>
</div>
        </div>
      </header>

      {/* ROUTES */}
      <Routes>
        <Route path="/" element={<HomePage onOpenEstimate={openEstimate} />} />
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
       <Route path="/terms" element={<TermsAndConditions />} />
      </Routes>

      {/* FOOTER */}
      <footer className="border-t border-slate-100 py-6 bg-white">
        <div className="mx-auto max-w-5xl px-4 text-[11px] text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p>
            © {new Date().getFullYear()} Your Property Guys. All rights
            reserved.
          </p>
          <div className="flex items-center gap-4">
            <span>Serving Memphis, Tennessee and surrounding areas.</span>
            <Link to="/terms" className="text-slate-500 hover:text-slate-800">
  Terms &amp; Conditions
</Link>
            <Link
              to="/privacypolicy"
              className="text-slate-500 hover:text-slate-800"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
        
      </footer>

      {/* ESTIMATE MODAL */}
      <EstimateModal open={estimateOpen} onClose={closeEstimate} />
    </div>
  );
}

export default App;